export type TeammateAvatarShape =
  | 'pentagon'
  | 'square'
  | 'circle'
  | 'hexagon'
  | 'losange';

export type TeammateAvatarEyes = '1' | '2' | '3' | '4' | '5';

/**
 * Avatar axes as persisted on the teammate (match the API `TeammateView` enums).
 * `TeammateColor` selects a base/dark palette; `TeammateEyesEnum` is the spelled-out
 * DB form of the eye variant (Prisma enum values can't start with a digit) and maps
 * to the UI `TeammateAvatarEyes` ('1'..'5').
 */
export type TeammateColor = 'A' | 'B' | 'C' | 'D' | 'E';
export type TeammateEyesEnum = 'one' | 'two' | 'three' | 'four' | 'five';

export interface TeammateAvatarConfig {
  baseColor: string;
  darkColor: string;
  shape: TeammateAvatarShape;
  eyes: TeammateAvatarEyes;
}

export type TeammateAvatarCropContext = 'x-small' | 'small' | 'large';

export const TEAMMATE_AVATAR_PRESETS: Record<
  'A' | 'B' | 'C' | 'D' | 'E',
  TeammateAvatarConfig
> = {
  A: {
    baseColor: '#B8CDFF',
    darkColor: '#7579FF',
    shape: 'pentagon',
    eyes: '1',
  },
  B: {
    baseColor: '#ABACFF',
    darkColor: '#A27CFF',
    shape: 'square',
    eyes: '2',
  },
  C: {
    baseColor: '#C7B1FF',
    darkColor: '#B66DFF',
    shape: 'circle',
    eyes: '1',
  },
  D: {
    baseColor: '#DFAFFF',
    darkColor: '#D272FF',
    shape: 'hexagon',
    eyes: '4',
  },
  E: {
    baseColor: '#F4BEF3',
    darkColor: '#EF75EA',
    shape: 'losange',
    eyes: '3',
  },
};

/** DB eyes enum (`one`..`five`) → UI eyes id (`'1'`..`'5'`). */
const EYES_BY_ENUM: Record<TeammateEyesEnum, TeammateAvatarEyes> = {
  one: '1',
  two: '2',
  three: '3',
  four: '4',
  five: '5',
};

/**
 * Resolve a teammate's avatar from its three independent axes (SB-3200 Decision F).
 * The base/dark palette comes from the per-color preset; shape passes through; the
 * spelled-out DB eyes enum maps to the UI '1'..'5' form.
 */
export function getTeammateAvatarFromParts(parts: {
  color: TeammateColor;
  shape: TeammateAvatarShape;
  eyes: TeammateEyesEnum;
}): TeammateAvatarConfig {
  const preset = TEAMMATE_AVATAR_PRESETS[parts.color];
  return {
    baseColor: preset.baseColor,
    darkColor: preset.darkColor,
    shape: parts.shape,
    eyes: EYES_BY_ENUM[parts.eyes],
  };
}
